import { useEffect, useRef, useState } from "react";
import { Ban } from "lucide-react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { GD } from "src/GD";
import { hasRole } from "src/permissions";
import { AUTH_STATE } from "src/services/AuthService";
import { BanForm } from "../pages/administration/BanDialogs";
import { getBanSetRequest, readBanResponse, type BanDraft } from "../pages/administration/banData";
import { sendBanMutation } from "../pages/administration/banActions";
import { contextBanEntity, type ContextBanEntity } from "./contextBanEntity";
import { useBanStatus } from "./useBanStatus";

const Action = styled.span`
	display: inline-flex; align-items: center; flex-wrap: wrap; gap: 8px;
	> button { display: inline-flex; align-items: center; justify-content: center; gap: 5px; min-height: 30px; padding: 5px 9px; border: 1px solid #ebc7cf; border-radius: 7px; background: #fff; color: #b42342; font: 12px/1.5 system-ui, sans-serif; cursor: pointer; white-space: nowrap; }
	> button:hover { background: #fff3f5; }
	> button[data-banned="true"] { background: #fff0f3; border-color: #e2a3b1; font-weight: 600; }
	> button:focus-visible { outline: 2px solid #167ca6; outline-offset: 2px; }
	> [role="status"] { color: #18774d; font: 12px/1.5 system-ui, sans-serif; }
`;

const AuthorizedAction = ({ entity, projectId, userId }: { entity: ContextBanEntity; projectId: number; userId: number }) => {
	const { t } = useTranslation();
	const status = useBanStatus({ ...entity, projectId, userId });
	const hasBans = !!status.snapshot?.rows.length;
	const actionTitle = t(hasBans ? "bans.status.manageFor" : "bans.forEntity", { value: entity.entity_value });
	const [open, setOpen] = useState(false);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");
	const [saved, setSaved] = useState(false);
	const alive = useRef(true);
	const pending = useRef<AbortController | null>(null);
	useEffect(() => {
		alive.current = true;
		const logout = GD.S_AUTH_LOG_OUT.subscribe(() => { alive.current = false; pending.current?.abort(); setOpen(false); setSaved(false); });
		return () => { alive.current = false; pending.current?.abort(); GD.S_AUTH_LOG_OUT.unsubscribe(logout); };
	}, []);
	const save = async (draft: BanDraft) => {
		if (!alive.current || pending.current) return;
		let request: IAPIRequest;
		try { request = getBanSetRequest(projectId, { ...draft, ...entity }); }
		catch (error) { setError(t(`bans.errors.${error instanceof Error ? error.message : "invalidFilters"}`)); return; }
		const controller = new AbortController(); pending.current = controller;
		setBusy(true); setError("");
		try {
			const response = readBanResponse(await sendBanMutation(request, userId, projectId, controller.signal));
			if (!alive.current || controller.signal.aborted) return;
			if (response.error) { setError(response.error); return; }
			if (response.data == null || response.data === false) { setError(t("bans.errors.unconfirmed")); return; }
			setOpen(false); setSaved(true);
		} catch (error) {
			if (alive.current && !controller.signal.aborted) setError(t(error instanceof Error && error.message === "contextChanged" ? "bans.errors.contextChanged" : "bans.errors.unconfirmed"));
		} finally {
			if (pending.current === controller) pending.current = null;
			if (alive.current && !controller.signal.aborted) setBusy(false);
		}
	};
	return <Action onClick={event => event.stopPropagation()}>
		<button type="button" data-banned={hasBans || undefined} aria-haspopup="dialog" title={actionTitle} aria-label={actionTitle} onClick={() => { if (!alive.current) return; setError(""); setSaved(false); setOpen(true); }}><Ban size={14} aria-hidden="true" />{hasBans ? t("bans.status.banned", { count: status.snapshot!.rows.length }) : t(status.busy ? "bans.status.checking" : status.error ? "bans.status.unknown" : "bans.add")}</button>
		{saved && <span role="status">{t("bans.saved")}</span>}
		{open && <BanForm projectId={projectId} userId={userId} entity={entity} busy={busy} error={error} onSave={draft => void save(draft)} onClose={() => { if (!pending.current) setOpen(false); }} />}
	</Action>;
};

/** Source context is mandatory. Keep the entry-point registry in docs/ban-entry-points.md updated. */
export const EntityBanAction = ({ type, value, projectId, userId }: { type: unknown; value: unknown; projectId: number; userId: number }) => {
	const [project] = GD.B_SELECTED_PROJECT.use();
	const auth = GD.S_AUTH_STATUS.use([]);
	const [profile] = GD.REQ_PROFILE.useValue(undefined, undefined, [project?.id, auth]);
	const entity = contextBanEntity(type, value);
	if (!entity || auth !== AUTH_STATE.AUTHORIZED || project?.id !== projectId || profile?.id !== userId || !hasRole(profile?.projects.find(item => item.id === projectId)?.roles, "admin")) return null;
	return <AuthorizedAction key={JSON.stringify([projectId, userId, entity.entity_type, entity.entity_value])} entity={entity} projectId={projectId} userId={userId} />;
};
